import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  importLibraryMediaFromPe,
  PeMediaKind,
  PeSearchItem,
  searchPeMedia,
  searchPeProjects,
  ConnectorLibraryMedia,
} from '../services/api';

const KIND_OPTIONS: { value: PeMediaKind; label: string }[] = [
  { value: 'edm', label: 'EDMs' },
  { value: 'ebook', label: 'Client engagement eBooks' },
  { value: 'periodic', label: 'Periodic eBooks' },
];

type Props = {
  /** When set, only show PE items that match this connector media type after import. */
  mediaTypeFilter?: 'image' | 'document' | null;
  onImported: (item: ConnectorLibraryMedia) => void;
};

export default function ProjectEssentialMediaPicker({ mediaTypeFilter = null, onImported }: Props) {
  const [kind, setKind] = useState<PeMediaKind>('edm');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectInput, setProjectInput] = useState('');
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [items, setItems] = useState<PeSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (kind === 'periodic') {
      setProjectId(null);
      setProjectInput('');
      setProjects([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await searchPeProjects(projectInput);
        if (!cancelled) setProjects(rows);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, projectInput]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await searchPeMedia({
          kind,
          q: debouncedQ,
          projectId: kind === 'periodic' ? undefined : projectId || undefined,
        });
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) {
          setItems([]);
          setError(e instanceof Error ? e.message : 'Failed to search Project Essential');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, debouncedQ, projectId]);

  const onPick = async (row: PeSearchItem) => {
    setImportingId(row.edmId);
    setError(null);
    try {
      const item = await importLibraryMediaFromPe(row.edmId);
      if (mediaTypeFilter && item.mediaType !== mediaTypeFilter) {
        setError(
          `This item imported as a ${item.mediaType}, but this slot expects a ${mediaTypeFilter === 'document' ? 'PDF' : mediaTypeFilter}.`,
        );
        return;
      }
      onImported(item);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {KIND_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            clickable
            color={kind === opt.value ? 'primary' : 'default'}
            onClick={() => setKind(opt.value)}
            sx={{
              fontWeight: kind === opt.value ? 700 : 500,
              ...(kind === opt.value
                ? { bgcolor: '#ed8936', color: '#fff', '&:hover': { bgcolor: '#dd7826' } }
                : {}),
            }}
          />
        ))}
      </Box>

      <TextField
        size="small"
        placeholder="Search by name…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.75, color: 'var(--color-text-faint)' }} />,
        }}
      />

      {kind !== 'periodic' && (
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(o) => o.name}
          value={projects.find((p) => p.id === projectId) || null}
          onChange={(_, v) => setProjectId(v?.id || null)}
          inputValue={projectInput}
          onInputChange={(_, v) => setProjectInput(v)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField {...params} label="Project filter" placeholder="Type to filter projects…" />
          )}
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: '#ed8936' }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No matching Project Essential items.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 360, overflow: 'auto' }}>
          {items.map((item) => {
            const busy = importingId === item.edmId;
            return (
              <Box
                key={item.edmId}
                component="button"
                type="button"
                disabled={!!importingId}
                onClick={() => { void onPick(item); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  textAlign: 'left',
                  border: '1px solid var(--color-border)',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: 'transparent',
                  cursor: importingId ? 'wait' : 'pointer',
                  opacity: importingId && !busy ? 0.55 : 1,
                  '&:hover': { bgcolor: 'rgba(237,137,54,0.08)', borderColor: '#ed8936' },
                }}
              >
                {item.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={item.thumbnailUrl}
                    alt=""
                    sx={{
                      width: 44,
                      height: 44,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      bgcolor: 'var(--color-bg)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 0.5,
                      bgcolor: 'rgba(0,0,0,0.04)',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--color-text-subtle)',
                    }}
                  >
                    PE
                  </Box>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap fontWeight={600}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {item.category || kind}
                    {item.projectNames.length > 0 ? ` · ${item.projectNames.slice(0, 2).join(', ')}` : ''}
                    {item.projectNames.length > 2 ? ` +${item.projectNames.length - 2}` : ''}
                  </Typography>
                </Box>
                {busy ? <CircularProgress size={18} sx={{ color: '#ed8936' }} /> : null}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
